import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import partnershipService from "../../../apis/Partnerships/partnershipService";
import { staffService } from "../../../apis/Profile/StaffApi";
import PartnershipForm from "../PartnershipForm/PartnershipForm";
import "../../Ambition/index.css";
import "./PartnershipsList.css";
import { SolutionIntro } from "../../Ambition/SolutionsLab/SolutionLabIntro";
import officeSupplies from "../../../assets/partnership_icons/office-supplies.svg";
import machinery from "../../../assets/partnership_icons/machinery.svg";
import cleaning from "../../../assets/partnership_icons/cleaning.svg";
import food from "../../../assets/partnership_icons/food.svg";
import rawMaterials from "../../../assets/partnership_icons/raw-materials.svg";
import packaging from "../../../assets/partnership_icons/packaging.svg";
import surplusMaterials from "../../../assets/partnership_icons/surplus-materials.svg";
import textiles from "../../../assets/partnership_icons/textiles.svg";
import transport from "../../../assets/partnership_icons/transport.svg";
import beforeArrow from "../../../assets/partnership_icons/before-arrow-partnership.svg";
import afterArrow from "../../../assets/partnership_icons/after-arrow-partnership.svg";
import {
  PartnershipDiscount,
  PartnershipFreeShipping,
} from "./PartnershipStamp";

const PartnershipsList = () => {
  const navigate = useNavigate();
  const { tabName } = useParams();
  const [categories, setCategories] = useState([]);
  const [partnerships, setPartnerships] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  // Changed from carouselPositions/carouselPages to expandedCategories
  const [expandedCategories, setExpandedCategories] = useState({});

  // Document type display mapping
  const getDocumentTypeDisplay = (documentType) => {
    const documentTypeMap = {
      supplier_report: "Supplier Report",
      lab_test: "Lab Test",
      certification: "Certification",
      reference: "Reference",
      certificate_logo: "Certificate Logo",
      company_logo: "Company Logo",
      other: "Other",
    };
    return documentTypeMap[documentType] || documentType;
  };

  const getCloudinaryUrl = (path) => {
    const cloudName = "azilisdjo";

    // Check if the path already includes the base URL
    if (path.startsWith("http")) {
      return path;
    }

    return `https://res.cloudinary.com/${cloudName}/${path}`;
  };

  // Navigation handler for partnership items
  const handlePartnershipClick = (partnership) => {
    const currentSearch = window.location.search;
    navigate(
      `/ambition/partnership/${tabName}/${partnership.id}${currentSearch}`,
      {
        state: { partnership },
      }
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if user is staff
        const staffStatus = await staffService.getUserStaffStatus();
        setIsStaff(staffStatus);

        // Fetch categories
        const categoriesData = await partnershipService.getCategories();
        setCategories(categoriesData);

        // Fetch partnerships for each category
        const partnershipsMap = {};
        const initialExpanded = {};

        for (const category of categoriesData) {
          const partnershipsData =
            await partnershipService.getPartnershipsByCategory(category.id);
          partnershipsMap[category.id] = partnershipsData;
          initialExpanded[category.id] = false; // All categories start collapsed
        }

        setPartnerships(partnershipsMap);
        setExpandedCategories(initialExpanded);
      } catch (err) {
        setError("Failed to load partnerships. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleForm = () => {
    setShowForm(!showForm);
  };

  // Handle expand (after arrow click)
  const handleExpand = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: true,
    }));
  };

  // Handle collapse (before arrow click)
  const handleCollapse = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: false,
    }));
  };

  const getCategoryIcon = (category) => {
    // First, check if the category has an icon file from backend
    if (category.icon) {
      return getCloudinaryUrl(category.icon);
    }

    // Second, check if the category has an icon class from backend
    if (category.icon_class) {
      return category.icon_class; // Return the CSS class name
    }

    // Fallback to hardcoded mapping if no backend icon is provided
    const iconMap = {
      // Office Supplies variations
      "office supplies": officeSupplies,
      "office-supplies": officeSupplies,
      office_supplies: officeSupplies,
      officesupplies: officeSupplies,

      // Machinery variations
      machinery: machinery,
      machines: machinery,
      equipment: machinery,

      // Cleaning variations
      cleaning: cleaning,
      "cleaning supplies": cleaning,
      "cleaning-supplies": cleaning,
      cleaning_supplies: cleaning,
      cleaningsupplies: cleaning,

      // Food variations
      food: food,
      "food & beverage": food,
      "food and beverage": food,
      "food-beverage": food,
      food_beverage: food,
      foodbeverage: food,
      beverages: food,

      // Raw Materials variations
      "raw materials": rawMaterials,
      "raw-materials": rawMaterials,
      raw_materials: rawMaterials,
      rawmaterials: rawMaterials,
      materials: rawMaterials,

      // Packaging variations
      packaging: packaging,
      "packaging materials": packaging,
      "packaging-materials": packaging,
      packaging_materials: packaging,
      packagingmaterials: packaging,

      // Surplus Materials variations
      "surplus materials": surplusMaterials,
      "surplus-materials": surplusMaterials,
      surplus_materials: surplusMaterials,
      surplusmaterials: surplusMaterials,
      surplus: surplusMaterials,

      // Textiles variations
      textiles: textiles,
      textile: textiles,
      fabrics: textiles,
      fabric: textiles,
      clothing: textiles,

      // Transport variations
      transport: transport,
      transportation: transport,
      logistics: transport,
      shipping: transport,
      delivery: transport,
    };

    const iconKey =
      iconMap[category.name.toLowerCase()] ||
      iconMap[category.name] ||
      iconMap[category.icon] ||
      "default";

    return iconKey;
  };

  const renderCategoryIcon = (category) => {
    const iconSrc = getCategoryIcon(category);

    // If it's a CSS icon class (starts with common icon prefixes)
    if (
      typeof iconSrc === "string" &&
      (iconSrc.startsWith("fas ") ||
        iconSrc.startsWith("far ") ||
        iconSrc.startsWith("fab ") ||
        iconSrc.startsWith("fa ") ||
        iconSrc.startsWith("material-icons") ||
        iconSrc.startsWith("icon-") ||
        iconSrc.includes("icon"))
    ) {
      return (
        <div className="partnerships-list__category-svg">
          <i
            className={iconSrc}
            style={{ fontSize: "24px", color: "#0c376a" }}
          ></i>
        </div>
      );
    }

    // If it's a file URL (starts with http or https)
    if (
      typeof iconSrc === "string" &&
      (iconSrc.startsWith("http://") || iconSrc.startsWith("https://"))
    ) {
      return (
        <div className="partnerships-list__category-svg">
          <img
            src={iconSrc}
            alt={`${category.name} icon`}
            className="partnerships-list__category-svg-img"
          />
        </div>
      );
    }

    // Fallback to imported SVG (hardcoded mapping)
    return (
      <div className="partnerships-list__category-svg">
        <img
          src={iconSrc}
          alt={`${category.name} icon`}
          className="partnerships-list__category-svg-img"
        />
      </div>
    );
  };

  const renderPartnershipItem = (partnership) => (
    <div
      key={partnership.id}
      className="partnerships-list__item partnerships-list__item--clickable"
      onClick={() => handlePartnershipClick(partnership)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handlePartnershipClick(partnership.id);
        }
      }}
    >
      <h4 className="partnerships-list__item-title">
        {partnership.featured && (
          <div className="partnerships-list__featured-badge">
            <i className="fas fa-star"></i>
          </div>
        )}
        {partnership.company_name}
      </h4>
      <div className="partnerships-list__item-header">
        {partnership.documents &&
          partnership.documents.find(
            (doc) => doc.document_type === "company_logo"
          ) && (
            <img
              src={getCloudinaryUrl(
                partnership.documents.find(
                  (doc) => doc.document_type === "company_logo"
                ).file
              )}
              alt={`${partnership.company_name} company_logo`}
              className="partnerships-list__item-logo"
            />
          )}

        <ul>
          <li className={!partnership.general_tagline ? "empty-li" : ""}>
            {partnership.general_tagline && (
              <div className="partnerships-list__item-info">
                <p className="partnerships-list__item-description">
                  {partnership.general_tagline}
                </p>
              </div>
            )}
          </li>

          <li
            className={
              !(
                partnership.documents &&
                partnership.documents.length > 0 &&
                partnership.documents.filter(
                  (doc) => doc.document_type === "certificate_logo"
                ).length > 0
              )
                ? "empty-li"
                : ""
            }
          >
            {partnership.documents && partnership.documents.length > 0 && (
              <div className="partnerships-list__item-documents">
                {partnership.documents
                  .filter((doc) => doc.document_type === "certificate_logo")
                  .slice(0, 2)
                  .map((doc) => {
                    const fileUrl = getCloudinaryUrl(doc.file);

                    if (doc.file.toLowerCase().endsWith(".pdf")) {
                      return (
                        <div
                          key={doc.id}
                          className="partnerships-list__document-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="partnerships-list__document-anchor"
                          >
                            <i className="fas fa-file-pdf"></i>
                            <span>
                              {doc.file_name ||
                                getDocumentTypeDisplay(doc.document_type)}
                            </span>
                          </a>
                        </div>
                      );
                    } else if (
                      ["png", "jpg", "jpeg", "gif"].some((ext) =>
                        doc.file.toLowerCase().endsWith(ext)
                      )
                    ) {
                      return (
                        <div
                          key={doc.id}
                          className="partnerships-list__document-thumbnail"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <img
                            src={fileUrl}
                            alt={
                              doc.file_name ||
                              `${
                                partnership.company_name
                              } ${getDocumentTypeDisplay(doc.document_type)}`
                            }
                            className="partnerships-list__document-image"
                          />
                        </div>
                      );
                    } else {
                      return (
                        <div
                          key={doc.id}
                          className="partnerships-list__document-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="partnerships-list__document-anchor"
                          >
                            <i className="fas fa-file"></i>
                            <span>
                              {doc.file_name ||
                                getDocumentTypeDisplay(doc.document_type)}
                            </span>
                          </a>
                        </div>
                      );
                    }
                  })}
              </div>
            )}
          </li>

          <li
            className={
              !(partnership.yfs_discount || partnership.free_shipping)
                ? "empty-li"
                : ""
            }
          >
            <PartnershipFreeShipping partnership={partnership} />
          </li>
        </ul>
      </div>
      <PartnershipDiscount partnership={partnership} />
    </div>
  );

  if (loading) {
    return (
      <div className="partnerships-list__loading">Loading partnerships...</div>
    );
  }

  if (error) {
    return <div className="partnerships-list__error">{error}</div>;
  }

  return (
    <div className="partnerships-list-container ambition__body">
      {showForm ? (
        <div>
          <button
            className="partnerships-list__back-button"
            onClick={toggleForm}
          >
            &larr; Back to Solutions
          </button>
          <PartnershipForm onClose={() => setShowForm(false)} />
        </div>
      ) : (
        <>
          <div className="partnerships-list__header">
            <SolutionIntro toggle={toggleForm} />
          </div>

          <div className="partnerships-list__categories">
            {categories.map((category) => {
              const categoryPartnerships = partnerships[category.id] || [];
              const hasItems = categoryPartnerships.length > 0;
              const needsExpandCollapse = categoryPartnerships.length > 3;
              const isExpanded = expandedCategories[category.id] || false;

              // Determine which items to show
              const visibleItems = isExpanded
                ? categoryPartnerships // Show all items when expanded
                : categoryPartnerships.slice(0, 3); // Show only first 3 when collapsed

              return (
                <div
                  key={category.id}
                  className="partnerships-list__category-card"
                  style={{ maxWidth: isExpanded ? "none" : "fit-content" }}
                >
                  <div className="partnerships-list__category-header">
                    <h3 className="partnerships-list__category-title">
                      {category.name}
                    </h3>
                    {renderCategoryIcon(category)}
                  </div>

                  <div className="partnerships-list__category-content">
                    {hasItems ? (
                      <div className="partnerships-list__items-container">
                        {/* Before Arrow - Show only when expanded and there are more than 3 items */}
                        {needsExpandCollapse && isExpanded && (
                          <button
                            className="partnerships-list__carousel-btn partnerships-list__carousel-btn--prev"
                            onClick={() => handleCollapse(category.id)}
                          >
                            <img
                              src={beforeArrow}
                              alt="Collapse"
                              width={20}
                              height={215}
                            />
                          </button>
                        )}

                        <div className="partnerships-list__items-wrapper">
                          <div
                            className={
                              isExpanded
                                ? "partnerships-list__items-trackrow"
                                : "partnerships-list__items-track"
                            }
                          >
                            {visibleItems.map(renderPartnershipItem)}
                          </div>
                        </div>

                        {/* After Arrow - Show only when collapsed and there are more than 3 items */}
                        {needsExpandCollapse && !isExpanded && (
                          <button
                            className="partnerships-list__carousel-btn partnerships-list__carousel-btn--next"
                            onClick={() => handleExpand(category.id)}
                          >
                            <img
                              src={afterArrow}
                              alt="Expand"
                              width={20}
                              height={215}
                            />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="partnerships-list__no-items">
                        No partnerships available in this category.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PartnershipsList;
